import ColorHash from "color-hash";
import { normalizeSelectionGroup, VALID_IG_HANDLE_REGEX } from "@stack/shared";

// Re-exported so existing dashboard imports keep working; the canonical
// implementation lives in @stack/shared and is shared with the backend.
export { normalizeSelectionGroup };

// Selection groups are free-text handles keyed per campaign. We color-code them
// so the management team can group like campaigns visually and catch typos at a
// glance (a slightly different handle hashes to a clearly different color).

// Reserved handles with special backend semantics (not just colors): `default`
// is the DB default; `exclusive` is a reserved keyword for special selection.
export const SELECTION_GROUP_RESERVED = ["default", "exclusive"];

// Short explanation shown beside each reserved handle in the edit dropdown.
export const SELECTION_GROUP_DESCRIPTIONS = {
  default: "Default group",
  // Needs to be short to avoid truncation in the UI.
  exclusive: "Blocks joining other campaigns in contract",
};

// Plain color presets, in display order.
export const SELECTION_GROUP_COLORS = [
  "red",
  "yellow",
  "green",
  "blue",
  "purple",
  "black",
  "white",
];

// Presets shown in the edit dropdown, reserved first then colors.
export const SELECTION_GROUP_PRESETS = [
  ...SELECTION_GROUP_RESERVED,
  ...SELECTION_GROUP_COLORS,
];

// { background, borderColor, color } tuned to Ant Design's palette so preset
// tags keep the light/bordered look used elsewhere in the dashboard.
const PRESET_STYLES = {
  default: { background: "#fafafa", borderColor: "#d9d9d9", color: "#595959" },
  exclusive: {
    background: "#fffbe6",
    borderColor: "#ffe58f",
    color: "#ad6800",
  },
  red: { background: "#fff1f0", borderColor: "#ffa39e", color: "#cf1322" },
  yellow: { background: "#feffe6", borderColor: "#fffb8f", color: "#ad8b00" },
  green: { background: "#f6ffed", borderColor: "#b7eb8f", color: "#389e0d" },
  blue: { background: "#e6f7ff", borderColor: "#91d5ff", color: "#096dd9" },
  purple: { background: "#f9f0ff", borderColor: "#d3adf7", color: "#531dab" },
  black: { background: "#1f1f1f", borderColor: "#000000", color: "#ffffff" },
  white: { background: "#ffffff", borderColor: "#d9d9d9", color: "#262626" },
};

// Selection groups follow the same rules as an Instagram handle (3-30 chars:
// a-z, 0-9, "." and "_", no leading/trailing/consecutive dots), so front and
// back end agree via one shared regex.
export const SELECTION_GROUP_REGEX = VALID_IG_HANDLE_REGEX;

// ColorHash maps a string to a stable HSL. We constrain its saturation/lightness
// to legible pastel bands so every hashed handle lands in the same
// light/bordered look as our presets, expanding the palette past 360 hue buckets
// (~360 x 3 x 3) so custom handles rarely collide while staying readable.
const colorHash = new ColorHash({
  saturation: [0, 0.66, 0.74],
  lightness: [0.92, 0.95, 0.98],
});

// Common color words -> hue, so an intuitive name (e.g. `orange`) renders in that
// color instead of a hashed one. Presets above take precedence; achromatic names
// (ACHROMATIC_COLORS) render neutral gray since they have no meaningful hue.
const COLOR_NAME_HUES = {
  orange: 32,
  amber: 45,
  gold: 48,
  lime: 84,
  mint: 150,
  emerald: 150,
  turquoise: 172,
  teal: 176,
  cyan: 186,
  aqua: 186,
  sky: 200,
  navy: 224,
  indigo: 245,
  violet: 272,
  lavender: 258,
  magenta: 300,
  fuchsia: 300,
  pink: 330,
  rose: 345,
  crimson: 348,
  salmon: 8,
  coral: 14,
};
const ACHROMATIC_COLORS = new Set(["gray", "grey", "silver"]);

// Builds the light/bordered pastel Tag style for a hue so named colors and
// hashed handles share one look. `saturation` 0 yields a neutral gray.
function buildHslStyle(hue, saturation, bgLightness) {
  const textSaturation = saturation === 0 ? 0 : Math.min(saturation + 6, 80);
  return {
    background: `hsl(${hue}, ${saturation}%, ${bgLightness}%)`,
    borderColor: `hsl(${hue}, ${saturation}%, 72%)`,
    color: `hsl(${hue}, ${textSaturation}%, 30%)`,
  };
}

// Approx hues of the fixed color presets (from their border swatches), keyed by
// name and spaced well over 2x HUE_GUARD apart so their guard bands never
// overlap. The name map also lets us push near-typos to the far side of a preset.
const PRESET_COLOR_HUES = {
  red: 4,
  yellow: 57,
  green: 96,
  blue: 205,
  purple: 271,
};
const PRESET_HUES = Object.values(PRESET_COLOR_HUES);
const HUE_GUARD = 18;

// Keep a hashed hue clear of the preset hues, nudging it to the nearer edge of
// the guard band. Stops a hashed handle from masquerading as a preset (e.g.
// `yelow` landing on `yellow`). Handles already >HUE_GUARD from every preset are
// left unchanged.
function avoidPresetHues(hue) {
  for (const presetHue of PRESET_HUES) {
    const delta = ((hue - presetHue + 540) % 360) - 180;
    if (Math.abs(delta) < HUE_GUARD) {
      const sign = delta >= 0 ? 1 : -1;
      return (presetHue + sign * HUE_GUARD + 360) % 360;
    }
  }
  return hue;
}

// Damerau-Levenshtein (optimal string alignment): plain edit distance, but a
// swap of two adjacent chars counts as one edit, so `pruple`/`purple` read as a
// single typo rather than two.
function damerauOsa(a, b) {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d = Array.from({ length: rows }, () => new Array(cols).fill(0));
  for (let i = 0; i < rows; i += 1) d[i][0] = i;
  for (let j = 0; j < cols; j += 1) d[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

// Hue of the hued preset this handle is most likely a typo of (within the same
// edit-distance threshold used for suggestions), or undefined. Lets us shove a
// near-typo to the opposite hue so a mistyped `purple` reads as clearly-not-
// purple instead of a lookalike sitting right beside it.
function nearestTypoPresetHue(key) {
  let best;
  for (const [name, hue] of Object.entries(PRESET_COLOR_HUES)) {
    const distance = damerauOsa(key, name);
    const threshold = name.length <= 4 ? 1 : 2;
    if (
      distance > 0 &&
      distance <= threshold &&
      (!best || distance < best.distance)
    ) {
      best = { hue, distance };
    }
  }
  return best && best.hue;
}

// Returns the inline Tag style for a selection group. Presets map to fixed
// colors; anything else is hashed to a stable, legible pastel (dark same-hue
// text on a light same-hue background) that stays consistent across the app.
export function getSelectionGroupStyle(group) {
  const key = normalizeSelectionGroup(group) || "default";

  if (PRESET_STYLES[key]) return PRESET_STYLES[key];

  if (ACHROMATIC_COLORS.has(key)) return buildHslStyle(220, 0, 95);

  if (COLOR_NAME_HUES[key] !== undefined) {
    return buildHslStyle(COLOR_NAME_HUES[key], 66, 95);
  }

  const [hashedHue, saturation, bgLightness] = colorHash.hsl(key);
  const typoHue = nearestTypoPresetHue(key);
  const hue =
    typoHue === undefined
      ? avoidPresetHues(Math.round(hashedHue))
      : avoidPresetHues((typoHue + 180) % 360);

  return buildHslStyle(
    hue,
    Math.round(saturation * 100),
    Math.round(bgLightness * 100)
  );
}

// Selection groups actually used across a set of campaigns, most-used first
// (ties broken alphabetically). Blank/missing counts as the `default` group, and
// presets and custom handles are both included so the whole contract's palette
// surfaces in one place. Invalid handles are dropped.
export function getUsedSelectionGroups(campaigns = []) {
  const counts = new Map();
  campaigns.forEach(campaign => {
    const key =
      normalizeSelectionGroup(campaign && campaign.selectionGroup) || "default";
    if (
      !SELECTION_GROUP_PRESETS.includes(key) &&
      !SELECTION_GROUP_REGEX.test(key)
    ) {
      return;
    }
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => key);
}

// Presets colors that can be suggested as likely typos for a given input.
export function getPresetSuggestions(input) {
  const key = normalizeSelectionGroup(input);
  if (!key) return [];

  return SELECTION_GROUP_PRESETS.map(preset => ({
    preset,
    distance: damerauOsa(key, preset),
  }))
    .filter(
      ({ preset, distance }) =>
        distance > 0 && distance <= (preset.length <= 4 ? 1 : 2)
    )
    .sort((a, b) => a.distance - b.distance || a.preset.localeCompare(b.preset))
    .map(({ preset }) => preset);
}
